
import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { useAuthActions } from "@/hooks/useAuthActions";
import { detectAnomalies } from "@/utils/security";
import { devLog, devWarn } from "@/utils/secureLogger";
import { clearUserData, initAuthCleanup } from "@/utils/authCleanup";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ 
    success: boolean;
    error: any;
    message?: string;
    data?: any;
  }>;
  signUp: (email: string, password: string, username: string, captchaToken?: string) => Promise<{ 
    error: any, 
    data: any, 
    isSignupDisabled?: boolean, 
    isExistingUser?: boolean,
    isPasswordWeak?: boolean,
    pendingCredentials?: { email: string, password: string, username: string }
  }>;
  signOut: () => Promise<void>;
  verifyOtp: (email: string, token: string, pendingCredentials?: { email: string, password: string, username: string }) => Promise<{ error: any, data: any }>;
  resendVerificationEmail: (email: string) => Promise<{ error: any, data: any }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const authActions = useAuthActions();

  useEffect(() => {
    // Initialize auth cleanup utilities
    initAuthCleanup();

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      devLog("Initial session loaded");
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      devLog("Auth state changed:", event);
      
      // Clear user data on sign out
      if (event === 'SIGNED_OUT') {
        clearUserData();
      }
      
      // Check for suspicious activity on auth events
      if (session?.user && ['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
        const isAnomalous = detectAnomalies(session.user.id, event);
        if (isAnomalous) {
          devWarn("Suspicious activity detected!");
          // In a production app, you might want to implement additional security measures here
          // Such as forcing re-authentication or notifying admins
        }
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider 
      value={{ 
        session, 
        user, 
        loading, 
        ...authActions
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
